import React, { useEffect, useMemo, useState } from "react";
import Header from "../../../../layout/header/Header";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ApplicantMappingRow from "./ApplicantMappingRow";
import { setIn, useFormik } from "formik";
import * as Yup from "yup";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getGroupApplicants,
  getMultiBookingList,
  resetGroupApplicants,
  resetMultiBookingListData,
  updateApplicantMapping,
} from "../../new-booking-form.slice";
import Loader from "../../../../components/Loader";
import { route } from "../../../../services/apiRoutes";
import SnackbarAlert from "../../../../shared-components/snackbar-alert/SnackbarAlert";
import { getErrorMessage } from "../../../../utilis/common-funtions";
import { useQuery } from "../../hooks/useQuery";
import { BOOKING_AS, BOOKING_FORM_STATUS } from "../../../../config/constant";

const applicantKeys = ["applicant1", "applicant2", "applicant3", "applicant4"];

function ApplicantMapping() {
  const { t } = useTranslation();
  const { groupId } = useParams();
  const history = useHistory();
  const query = useQuery();
  const oppId = query.get("oppId");
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);

  const { isLoading, groupApplicants, multiBookingListData } = useSelector(
    (state) => state.newBookingForm
  );

  const { filledUnits, unfilledUnits, firstFilledUnit } = useMemo(() => {
    const { opportunities = [], firstFilledUnit: firstFilledData } =
      multiBookingListData || {};
    const filterOpp = opportunities?.filter(
      (d) => d?.status !== BOOKING_FORM_STATUS.IN_PROGRESS
    );

    if (!filterOpp?.length) {
      return { filledUnits: [], unfilledUnits: [], firstFilledUnit: null };
    }

    const formatMapping = (field, mapping = {} ) =>
      Object.fromEntries(
        Object.entries(mapping).map(([k, v]) => [k, v?.[field] ?? null])
      );

    const [filledUnits, unfilledUnits] = filterOpp?.reduce(
      ([filled, unfilled], opp) => {
        const unitData = {
          unitNo: opp?.unitno,
          opportunityId: opp?.Id,
          bookingAs: firstFilledData?.bookingAs,
          enquiryId: opp?.enqrefno,
          mapping: formatMapping(
            opp?.isCompleted ? "name" : "value",
            firstFilledData?.mapping
          ),
        };
        return opp?.isCompleted
          ? [[...filled, unitData], unfilled]
          : [filled, [...unfilled, unitData]];
      },
      [[], []]
    );

    if (oppId && unfilledUnits?.length > 0) {
      const index = unfilledUnits?.findIndex((u) => u?.opportunityId === oppId);
      if (index > -1) {
        const [matched] = unfilledUnits?.splice(index, 1);
        unfilledUnits?.unshift(matched);
      }
    }

    const firstFilledUnit =
      filledUnits.find(
        (u) => u.opportunityId === firstFilledData?.opportunityId
      ) || null;

    return { filledUnits, unfilledUnits, firstFilledUnit };
  }, [multiBookingListData, oppId]);

  const isIndividualFlow = firstFilledUnit?.bookingAs === BOOKING_AS.INDIVIDUAL;

  const applicantOptions = useMemo(() => {
    if (!groupApplicants?.length) return [];
    return [
      ...groupApplicants,
      {
        value: "new_applicant",
        name: isIndividualFlow ? "New Applicant" : "New Authorized Signatory",
      },
    ];
  }, [groupApplicants, isIndividualFlow]);

  const [snackbarConfig, setSnackbarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const formik = useFormik({
    initialValues: {
      groupMapping: [],
      sameApplicants: false,
      sameApplicantType: false,
      moreApplicants: false,
    },
    validationSchema: Yup.object({
      groupMapping: Yup.array().of(
        Yup.object({
          unitNo: Yup.string(),
          opportunityId: Yup.string(),
          bookingAs: Yup.string(),
          mapping: Yup.object(
            applicantKeys.reduce((acc, key) => {
              acc[key] = Yup.string()
                .nullable()
                .when({
                  is: (val) => val !== null,
                  then: (schema) =>
                    schema.required(
                      t("applicantMapping.validations.required", {
                        entity: isIndividualFlow
                          ? "Applicant"
                          : "Authorized signatory",
                      })
                    ),
                  otherwise: (schema) => schema.notRequired(),
                })
                .test(
                  "unique",
                  t("applicantMapping.validations.unique", {
                    entity: isIndividualFlow
                      ? "Applicant"
                      : "Authorized signatory",
                  }),
                  function (value) {
                    if (!value || value.trim() === "") return true;
                    if (value === "new_applicant") return true;

                    const otherValues = Object.entries(this.parent)
                      .filter(([k]) => k !== key)
                      .map(([_, v]) => v)
                      .filter(
                        (v) => v && v.trim() !== "" && v !== "new_applicant"
                      );

                    return !otherValues.includes(value);
                  }
                );
              return acc;
            }, {})
          ),
        })
      ),
      sameApplicants: Yup.boolean(),
      sameApplicantType: Yup.boolean(),
      moreApplicants: Yup.boolean(),
    }).test(
      "at-least-one",
      t("applicantMapping.validations.checkbox"),
      function (values) {
        const { sameApplicants, moreApplicants } = values;

        if (sameApplicants || moreApplicants) {
          return true;
        }
        throw this.createError({
          path: "sameApplicants",
          message: t("applicantMapping.validations.checkbox"),
        });
      }
    ),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      await dispatch(
        updateApplicantMapping({
          path: route.UPDATE_APPLICANT_MAPPING,
          payload: {
            groupId,
            ...values,
          },
        })
      )
        .unwrap()
        .then((response) => {
          setSnackbarConfig({
            open: true,
            message:
              response?.message || "Applicant mapping updated successfully",
            severity: "success",
          });
          history.push(
            `/booking-form/${values?.groupMapping?.[0]?.opportunityId}`
          );
        })
        .catch((error) => {
          setSnackbarConfig({
            open: true,
            message: getErrorMessage(error),
            severity: "error",
          });
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          dispatch(getGroupApplicants(groupId)).unwrap(),
          dispatch(getMultiBookingList({ groupId })).unwrap(),
        ]);
      } catch {
        history.push("/404");
      }
    };

    fetchData();

    return () => {
      dispatch(resetGroupApplicants());
      dispatch(resetMultiBookingListData());
    };
  }, [dispatch, groupId, history]);

  useEffect(() => {
    if (unfilledUnits?.length > 0 && !multiBookingListData?.isFirstFormFilled) {
      history.push(`/booking-form/${unfilledUnits[0]?.opportunityId}`);
    } else if (unfilledUnits?.length === 0 && filledUnits?.length > 0) {
      history.push(`/booking-form/${filledUnits[0]?.opportunityId}`);
    }
  }, [history, multiBookingListData, unfilledUnits, filledUnits]);

  useEffect(() => {
    if (formik.values?.sameApplicants) {
      formik.setFieldValue("groupMapping", unfilledUnits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values?.sameApplicants]);

  useEffect(() => {
    if (formik.values?.moreApplicants) {
      formik.setFieldValue("groupMapping", [unfilledUnits?.[0]]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values?.moreApplicants]);

  const showSameApplicantsCheckbox = !formik.values?.moreApplicants;
  const showSameApplicantTypeCheckbox = formik.values?.sameApplicants;
  const showMoreApplicantsCheckbox = !formik.values?.sameApplicants;
  const showCheckboxError =
    formik.errors.sameApplicants && formik.touched.sameApplicants;
  const areCheckboxesDisabled = activeStep === 1;

  const canAddApplicant = formik.values?.moreApplicants;
  const canRemoveApplicant = formik.values?.moreApplicants;
  const areApplicantsDisabled =
    formik.values?.sameApplicants && formik.values?.sameApplicantType;

  const handleResetForm = () => {
    setActiveStep(0);
    formik.resetForm();
  };

  const setAllTouched = (errors) => {
    let touched = {};

    const markTouched = (errObj, path = "") => {
      Object.keys(errObj).forEach((key) => {
        const currentPath = path ? `${path}.${key}` : key;

        if (Array.isArray(errObj[key])) {
          errObj[key].forEach((item, index) => {
            markTouched(item, `${currentPath}[${index}]`);
          });
        } else if (typeof errObj[key] === "object" && errObj[key] !== null) {
          markTouched(errObj[key], currentPath);
        } else {
          touched = setIn(touched, currentPath, true);
        }
      });
    };

    markTouched(errors);
    return touched;
  };

  const handleNext = async () => {
    const errors = await formik.validateForm();

    if (Object.keys(errors).length === 0) {
      if (activeStep === 0) {
        setActiveStep(1);
      } else {
        formik.submitForm();
      }
    } else {
      formik.setTouched(setAllTouched(errors));
    }
  };

  const handlePrev = () => {
    handleResetForm();
  };

  const handleSnackbarClose = () => {
    setSnackbarConfig({
      open: false,
      message: "",
      severity: "",
    });
  };

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <Helmet>
        <title>{t("applicantMapping.helmet.title")}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Header />
      <Stack>
        <Grid
          container
          spacing={0}
          sx={{
            p: 0,
            paddingLeft: {
              xs: "16px",
              sm: "16px",
              md: "16px",
              lg: "7%",
            },
            paddingRight: {
              xs: "16px",
              sm: "16px",
              md: "16px",
              lg: "7%",
            },
            mb: {
              xs: 2,
              sm: 4,
              md: 5,
            },
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              textAlign: {
                xs: "center",
                sm: "center",
                md: "center",
              },
              pt: {
                xs: "16px",
                sm: "30px",
                md: "36px",
              },
              pb: {
                xs: "16px",
                sm: "18px",
                md: "20px",
              },
            }}
          >
            <Typography
              sx={{
                color: "#1A407D",
                fontSize: {
                  xs: "20px",
                  sm: "20px",
                  md: "24px",
                },
                fontWeight: "700",
                lineHeight: {
                  xs: "28px",
                  sm: "32px",
                  md: "36px",
                },
                mb: {
                  xs: 0.5,
                  sm: 1,
                  md: 1,
                },
              }}
            >
              {t("applicantMapping.pageHeader.mainTitle")}
            </Typography>
            <Typography
              sx={{
                color: "#1C252E",
                fontSize: "14px",
                fontWeight: "500",
                lineHeight: "22px",
              }}
            >
              {t("applicantMapping.pageHeader.subTitle")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Box
              sx={{
                borderRadius: "8px",
                border: "1px solid rgba(26, 64, 125, 0.30)",
                padding: {
                  xs: "16px",
                  sm: "16px",
                  lg: "26px 39px 26px 39px",
                },
              }}
            >
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: "87px" }} />
                    <Typography
                      sx={{
                        color: "#1C252E",
                        fontSize: "16px",
                        fontWeight: "600",
                        lineHeight: "36px",
                        textAlign: "center",
                      }}
                    >
                      {t("applicantMapping.sections.applicantMappingBox", {
                        entity: isIndividualFlow
                          ? "Applicant"
                          : "Authorized Signatory",
                      })}
                    </Typography>
                    <Button
                      type="button"
                      sx={{
                        padding: "12px 20px",
                        borderRadius: "8px",
                        background: "#FFF",
                        color: "#1A407D",
                        border: "1px solid #D0D5DD",
                        fontSize: "16px",
                        fontWeight: "600",
                        lineHeight: "24px",
                        textTransform: "capitalize",
                      }}
                      onClick={handleResetForm}
                    >
                      {t("applicantMapping.buttons.reset")}
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        fontWeight: "500",
                        fontSize: "14px",
                        color: "#1C252E",
                        lineHeight: "22px",
                      }}
                    >
                      {t("applicantMapping.labels.listOfApplicants", {
                        entity: isIndividualFlow
                          ? "applicants"
                          : "authorized signatories",
                        unitNo: firstFilledUnit?.unitNo,
                      })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <ApplicantMappingRow
                      formik={formik}
                      unitNo={firstFilledUnit?.unitNo}
                      applicantsPath=""
                      applicantKeys={applicantKeys}
                      applicantOptions={applicantOptions}
                      applicants={firstFilledUnit?.mapping}
                      canAdd={false}
                      canRemove={false}
                      isDisabled={true}
                      readOnly={true}
                      isIndividualFlow={isIndividualFlow}
                    />
                  </Grid>
                  {activeStep === 1 && (
                    <>
                      <Grid item xs={12}>
                        <Typography
                          sx={{
                            fontWeight: "500",
                            fontSize: "14px",
                            color: "#1C252E",
                            lineHeight: "22px",
                          }}
                        >
                          {t("applicantMapping.labels.selectApplicants", {
                            entity: isIndividualFlow
                              ? "applicants"
                              : "authorized signatories",
                          })}
                        </Typography>
                      </Grid>
                      {formik.values?.groupMapping?.map((unit, index) => (
                        <Grid key={`${unit?.opportunityId ?? ""}-${unit?.unitNo ?? ""}-${index}`} item xs={12}>
                          <ApplicantMappingRow
                            key={`${unit?.opportunityId ?? ""}-${unit?.unitNo ?? ""}-${index}`}
                            formik={formik}
                            unitNo={unit?.unitNo}
                            applicantsPath={`groupMapping[${index}].mapping`}
                            applicantKeys={applicantKeys}
                            applicantOptions={applicantOptions}
                            canAdd={canAddApplicant}
                            canRemove={canRemoveApplicant}
                            isDisabled={areApplicantsDisabled}
                            isIndividualFlow={isIndividualFlow}
                          />
                        </Grid>
                      ))}
                    </>
                  )}
                  <Grid item xs={12}>
                    <FormGroup>
                      {showSameApplicantsCheckbox && (
                        <FormControlLabel
                          key="sameApplicants"
                          control={
                            <Checkbox
                              id="sameApplicants"
                              name="sameApplicants"
                              size="small"
                              color="primary"
                              checked={formik.values?.sameApplicants}
                              disabled={areCheckboxesDisabled}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                formik.setFieldValue("sameApplicants", checked);
                                formik.setFieldValue(
                                  "sameApplicantType",
                                  false
                                );
                              }}
                              sx={{
                                "&.Mui-checked": {
                                  color: "#1A407D",
                                },
                                "&.Mui-disabled": {
                                  color: "rgba(0, 0, 0, 0.26)",
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{
                                fontWeight: "500",
                                fontSize: "14px",
                                color: "#212121",
                                lineHeight: "22px",
                                ".Mui-disabled &": {
                                  color: "rgba(0, 0, 0, 0.26)",
                                },
                              }}
                            >
                              {t("applicantMapping.labels.sameApplicants", {
                                entity: isIndividualFlow
                                  ? "applicants"
                                  : "authorized signatories",
                              })}
                            </Typography>
                          }
                        />
                      )}
                      {showSameApplicantTypeCheckbox && (
                        <FormControlLabel
                          key="sameApplicantType"
                          control={
                            <Checkbox
                              id="sameApplicantType"
                              name="sameApplicantType"
                              size="small"
                              color="primary"
                              checked={formik.values?.sameApplicantType}
                              onChange={formik.handleChange}
                              disabled={areCheckboxesDisabled}
                              sx={{
                                "&.Mui-checked": {
                                  color: "#1A407D",
                                },
                                "&.Mui-disabled": {
                                  color: "rgba(0, 0, 0, 0.26)",
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{
                                fontWeight: "500",
                                fontSize: "14px",
                                color: "#212121",
                                lineHeight: "22px",
                                ".Mui-disabled &": {
                                  color: "rgba(0, 0, 0, 0.26)",
                                },
                              }}
                            >
                              {t("applicantMapping.labels.sameApplicantType", {
                                entity: isIndividualFlow
                                  ? "Applicant"
                                  : "Authorized signatory",
                              })}
                            </Typography>
                          }
                        />
                      )}
                      {showMoreApplicantsCheckbox && (
                        <FormControlLabel
                          key="moreApplicants"
                          control={
                            <Checkbox
                              id="moreApplicants"
                              name="moreApplicants"
                              size="small"
                              color="primary"
                              checked={formik.values?.moreApplicants}
                              onChange={formik.handleChange}
                              disabled={areCheckboxesDisabled}
                              sx={{
                                "&.Mui-checked": {
                                  color: "#1A407D",
                                },
                                "&.Mui-disabled": {
                                  color: "rgba(0, 0, 0, 0.26)",
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{
                                fontWeight: "500",
                                fontSize: "14px",
                                color: "#212121",
                                lineHeight: "22px",
                                ".Mui-disabled &": {
                                  color: "rgba(0, 0, 0, 0.26)",
                                },
                              }}
                            >
                              {t("applicantMapping.labels.moreApplicants", {
                                entity: isIndividualFlow
                                  ? "applicants"
                                  : "authorized signatories",
                              })}
                            </Typography>
                          }
                        />
                      )}
                      {showCheckboxError && (
                        <FormHelperText error>
                          {formik.errors?.sameApplicants}
                        </FormHelperText>
                      )}
                    </FormGroup>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="end"
                    alignItems="center"
                    gap={3}
                    flexDirection={{
                      xs: "column",
                      sm: "row",
                    }}
                  >
                    {activeStep === 1 && (
                      <Button
                        startIcon={
                          <Box
                            sx={{
                              width: "24px",
                              height: "24px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <NavigateNextIcon
                              sx={{
                                transform: "rotate(180deg)",
                                transition: "transform 0.3s ease",
                              }}
                            />
                          </Box>
                        }
                        type="button"
                        sx={{
                          width: {
                            xs: "48%",
                            sm: "157px",
                            md: "157px",
                            lg: "157px",
                          },
                          height: "48px",
                          borderRadius: "8px",
                          background: "#fff",
                          color: "#1A407D",
                          fontSize: "16px",
                          fontWeight: "600",
                          lineHeight: "24px",
                          textTransform: "capitalize",
                          border: "1px solid #D0D5DD",
                          "&:hover": {
                            backgroundColor: "#092552",
                            color: "#fff",
                          },
                        }}
                        onClick={handlePrev}
                      >
                        {t("applicantMapping.buttons.previous")}
                      </Button>
                    )}
                    <Button
                      endIcon={
                        <Box
                          sx={{
                            width: "24px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <NavigateNextIcon />
                        </Box>
                      }
                      type="button"
                      sx={{
                        width: {
                          xs: "48%",
                          sm: "157px",
                          md: "157px",
                          lg: "157px",
                        },
                        height: "48px",
                        borderRadius: "8px",
                        background: "#1A407D",
                        color: "#fff",
                        fontSize: "16px",
                        fontWeight: "600",
                        lineHeight: "24px",
                        textTransform: "capitalize",
                        "&:hover": {
                          backgroundColor: "#092552",
                        },
                      }}
                      onClick={handleNext}
                      disabled={formik.isSubmitting}
                    >
                      {t("applicantMapping.buttons.next")}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Grid>
        </Grid>
      </Stack>
      <SnackbarAlert
        open={snackbarConfig.open}
        message={snackbarConfig.message}
        severity={snackbarConfig.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
}

export default ApplicantMapping;
